// Formatting toolbar for the Markdown editor (both Rich and Source mode).
import { redo as cmRedo, undo as cmUndo } from "@codemirror/commands";
import type { StateCommand } from "@codemirror/state";
import type { EditorView, KeyBinding } from "@codemirror/view";
import type { ReactNode } from "react";
import { yUndoManagerKeymap } from "y-codemirror.next";
import { Icon, type IconName } from "../components/icons";
import {
  cycleHeading,
  indentList,
  insertHorizontalRule,
  insertLink,
  insertTable,
  outdentList,
  setHeading,
  toggleBold,
  toggleBulletList,
  toggleCodeBlock,
  toggleInlineCode,
  toggleItalic,
  toggleOrderedList,
  toggleQuote,
  toggleStrike,
  toggleTaskList,
  type FormatName,
} from "./commands";

const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);

/** "Mod-Shift-c" → "Ctrl+Shift+C" / "⌘⇧C". */
export function shortcutLabel(key: string): string {
  const parts = key.split("-");
  const last = parts.pop() ?? "";
  const mods = parts.map((p) => {
    if (p === "Mod") return isMac ? "⌘" : "Ctrl";
    if (p === "Shift") return isMac ? "⇧" : "Shift";
    if (p === "Alt") return isMac ? "⌥" : "Alt";
    return p;
  });
  const k = last.length === 1 ? last.toUpperCase() : last;
  return isMac ? [...mods, k].join("") : [...mods, k].join("+");
}

/** Undo/redo: Yjs UndoManager when collaborative (yCollab installed), CM history otherwise. */
export function undoView(view: EditorView): boolean {
  return yUndoManagerKeymap[0].run?.(view) || cmUndo(view);
}
export function redoView(view: EditorView): boolean {
  return yUndoManagerKeymap[1].run?.(view) || cmRedo(view);
}

interface Action {
  id: string;
  icon: IconName;
  label: string;
  key?: string;
  run: StateCommand | ((view: EditorView) => boolean);
  active?: FormatName;
  view?: boolean; // run takes the view instead of {state, dispatch}
}

const wrap = (c: StateCommand) => (view: EditorView) => c({ state: view.state, dispatch: view.dispatch });

export const ACTIONS: Action[][] = [
  [
    { id: "bold", icon: "bold", label: "Bold", key: "Mod-b", run: toggleBold, active: "bold" },
    { id: "italic", icon: "italic", label: "Italic", key: "Mod-i", run: toggleItalic, active: "italic" },
    { id: "strike", icon: "strike", label: "Strikethrough", key: "Mod-Shift-x", run: toggleStrike, active: "strike" },
    { id: "code", icon: "code", label: "Inline code", key: "Mod-Shift-e", run: toggleInlineCode, active: "code" },
  ],
  [
    { id: "bullet", icon: "bullet", label: "Bullet list", key: "Mod-Shift-8", run: toggleBulletList, active: "bullet" },
    { id: "ordered", icon: "ordered", label: "Numbered list", key: "Mod-Shift-7", run: toggleOrderedList, active: "ordered" },
    { id: "task", icon: "task", label: "Task (toggles done on a task line)", key: "Mod-Shift-c", run: toggleTaskList, active: "task" },
  ],
  [
    { id: "quote", icon: "quote", label: "Quote", key: "Mod-Shift-9", run: toggleQuote, active: "quote" },
    { id: "codeblock", icon: "codeblock", label: "Code block", key: "Mod-Alt-c", run: toggleCodeBlock, active: "codeblock" },
    { id: "link", icon: "link", label: "Link", key: "Mod-k", run: insertLink, active: "link" },
    { id: "hr", icon: "hr", label: "Horizontal rule", run: insertHorizontalRule },
    { id: "table", icon: "table", label: "Table", run: insertTable },
  ],
  [
    { id: "outdent", icon: "outdent", label: "Outdent list item", key: "Shift-Tab", run: outdentList },
    { id: "indent", icon: "indent", label: "Indent list item", key: "Tab", run: indentList },
  ],
  [
    { id: "undo", icon: "undo", label: "Undo", key: "Mod-z", run: undoView, view: true },
    { id: "redo", icon: "redo", label: "Redo", key: isMac ? "Mod-Shift-z" : "Mod-y", run: redoView, view: true },
  ],
];

/** Formatting key bindings (Tab/Shift-Tab only act on list lines and fall through otherwise). */
export function formattingKeymap(): KeyBinding[] {
  const out: KeyBinding[] = [];
  for (const group of ACTIONS) {
    for (const a of group) {
      if (!a.key || a.view) continue;
      out.push({ key: a.key, run: wrap(a.run as StateCommand), preventDefault: a.key !== "Tab" && a.key !== "Shift-Tab" });
    }
  }
  out.push({ key: "Mod-Alt-0", run: wrap(setHeading(0)), preventDefault: true });
  out.push({ key: "Mod-Alt-1", run: wrap(setHeading(1)), preventDefault: true });
  out.push({ key: "Mod-Alt-2", run: wrap(setHeading(2)), preventDefault: true });
  out.push({ key: "Mod-Alt-3", run: wrap(setHeading(3)), preventDefault: true });
  out.push({ key: "Mod-Alt-h", run: wrap(cycleHeading), preventDefault: true });
  return out;
}

export function EditorToolbar({
  getView,
  active,
  heading,
  modeSwitch,
  children,
}: {
  getView: () => EditorView | null;
  active: Set<FormatName>;
  heading: number;
  modeSwitch: ReactNode;
  children?: ReactNode;
}) {
  const exec = (a: Action) => {
    const view = getView();
    if (!view) return;
    if (a.view) (a.run as (v: EditorView) => boolean)(view);
    else wrap(a.run as StateCommand)(view);
    view.focus();
  };
  return (
    <div className="md-editor-toolbar" role="toolbar" aria-label="Formatting">
      {modeSwitch}
      <span className="tb-sep" aria-hidden="true" />
      <div className="tb-scroll">
        <select
          className="tb-heading"
          aria-label="Text style"
          title={`Text style (${shortcutLabel("Mod-Alt-1")}…3, ${shortcutLabel("Mod-Alt-0")} paragraph)`}
          value={String(heading > 3 ? 3 : heading)}
          onMouseDown={(e) => e.stopPropagation()}
          onChange={(e) => {
            const view = getView();
            if (!view) return;
            wrap(setHeading(Number(e.target.value)))(view);
            view.focus();
          }}
        >
          <option value="0">Paragraph</option>
          <option value="1">Heading 1</option>
          <option value="2">Heading 2</option>
          <option value="3">Heading 3</option>
        </select>
        {ACTIONS.map((group, gi) => (
          <div className="tb-group" key={gi} role="group">
            {group.map((a) => {
              const pressed = a.active ? active.has(a.active) : undefined;
              const title = a.key ? `${a.label} (${shortcutLabel(a.key)})` : a.label;
              return (
                <button
                  key={a.id}
                  type="button"
                  className={`tb-btn${pressed ? " on" : ""}`}
                  title={title}
                  aria-label={a.label}
                  aria-pressed={pressed}
                  onMouseDown={(e) => e.preventDefault()} // keep the editor selection
                  onClick={() => exec(a)}
                >
                  <Icon name={a.icon} />
                </button>
              );
            })}
          </div>
        ))}
      </div>
      {children}
    </div>
  );
}
